import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Message as MessageIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { 
  loadConversationHistory, 
  saveConversationHistory,
  Conversation,
  ChatMessage
} from '../services/conversationService';
import { whatsappService } from '../services/whatsappService';
import { generateRAGResponse } from '../services/ragService';

const ConversationHistoryPage: React.FC = () => {
  // Estado para o histórico de conversas
  const [conversationHistory, setConversationHistory] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string>('');
  const [replyMessage, setReplyMessage] = useState('');
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  
  // Estado para nova conversa
  const [openNewConversationDialog, setOpenNewConversationDialog] = useState(false);
  const [newConversation, setNewConversation] = useState({
    customerName: '',
    customerPhone: '',
    initialMessage: ''
  });
  
  // Estado para notificações
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });
  
  // Templates de mensagens rápidas
  const templates = [
    {
      id: '1',
      name: 'Boas-vindas',
      content: 'Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?'
    },
    {
      id: '2',
      name: 'Informações',
      content: 'Gostaria de saber mais sobre algum imóvel específico ou região?'
    },
    {
      id: '3',
      name: 'Agendamento',
      content: 'Podemos agendar uma visita ao imóvel. Qual seria o melhor dia e horário para você?'
    }
  ];
  
  // Carregar histórico de conversas ao iniciar
  useEffect(() => {
    const conversationHistoryData = loadConversationHistory();
    
    // Se não houver conversas salvas, adicionar exemplos
    if (conversationHistoryData.length === 0) {
      // Exemplo 1: Cliente interessado em apartamento
      const conversation1: Conversation = {
        id: '1',
        customerName: 'João Silva',
        customerPhone: '5511987654321',
        lastMessage: 'Vou verificar a disponibilidade e retorno em breve com mais informações sobre o apartamento no Centro.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 horas atrás
        status: 'active',
        messages: [
          {
            id: '1-1',
            text: 'Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?',
            sender: 'bot',
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000) // 3 horas atrás
          },
          {
            id: '1-2',
            text: 'Olá, estou procurando um apartamento de 2 quartos no Centro',
            sender: 'user',
            timestamp: new Date(Date.now() - 2.5 * 60 * 60 * 1000) // 2.5 horas atrás
          },
          {
            id: '1-3',
            text: 'Temos várias opções de apartamentos de 2 quartos no Centro. Você tem preferência por alguma faixa de preço específica?',
            sender: 'bot',
            timestamp: new Date(Date.now() - 2.4 * 60 * 60 * 1000) // 2.4 horas atrás
          },
          {
            id: '1-4',
            text: 'Estou procurando algo até R$ 2.500 de aluguel',
            sender: 'user',
            timestamp: new Date(Date.now() - 2.3 * 60 * 60 * 1000) // 2.3 horas atrás
          },
          {
            id: '1-5',
            text: 'Vou verificar a disponibilidade e retorno em breve com mais informações sobre o apartamento no Centro.',
            sender: 'bot',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000) // 2 horas atrás
          }
        ]
      };
      
      // Exemplo 2: Cliente interessado em casa
      const conversation2: Conversation = {
        id: '2',
        customerName: 'Maria Oliveira',
        customerPhone: '5511976543210',
        lastMessage: 'Podemos agendar uma visita para amanhã às 14h na casa do Jardim das Flores.',
        timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutos atrás
        status: 'active',
        messages: [
          {
            id: '2-1',
            text: 'Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?',
            sender: 'bot',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000) // 2 horas atrás
          },
          {
            id: '2-2',
            text: 'Boa tarde! Estou procurando uma casa para comprar no bairro Jardim das Flores',
            sender: 'user',
            timestamp: new Date(Date.now() - 1.5 * 60 * 60 * 1000) // 1.5 horas atrás
          },
          {
            id: '2-3',
            text: 'Temos algumas casas disponíveis no Jardim das Flores. Você está procurando para moradia própria ou investimento?',
            sender: 'bot',
            timestamp: new Date(Date.now() - 1.4 * 60 * 60 * 1000) // 1.4 horas atrás
          },
          {
            id: '2-4',
            text: 'Para moradia própria. Preciso de pelo menos 3 quartos e quintal grande',
            sender: 'user',
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000) // 1 hora atrás
          },
          {
            id: '2-5',
            text: 'Temos uma excelente opção no Jardim das Flores com 3 quartos, sendo 1 suíte, quintal espaçoso e área gourmet. O valor é R$ 450.000,00. Teria interesse em conhecer?',
            sender: 'bot',
            timestamp: new Date(Date.now() - 50 * 60 * 1000) // 50 minutos atrás
          },
          {
            id: '2-6',
            text: 'Sim, tenho interesse! Quando poderia visitar?',
            sender: 'user',
            timestamp: new Date(Date.now() - 40 * 60 * 1000) // 40 minutos atrás
          },
          {
            id: '2-7',
            text: 'Podemos agendar uma visita para amanhã às 14h na casa do Jardim das Flores.',
            sender: 'bot',
            timestamp: new Date(Date.now() - 30 * 60 * 1000) // 30 minutos atrás
          }
        ]
      };
      
      // Exemplo 3: Cliente com dúvidas sobre financiamento
      const conversation3: Conversation = {
        id: '3',
        customerName: 'Carlos Mendes',
        customerPhone: '5511965432109',
        lastMessage: 'Vou enviar os documentos necessários para a simulação de financiamento por e-mail.',
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 dias atrás
        status: 'closed',
        messages: [
          {
            id: '3-1',
            text: 'Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?',
            sender: 'bot',
            timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000) // 6 dias atrás
          },
          {
            id: '3-2',
            text: 'Olá, gostaria de informações sobre financiamento imobiliário',
            sender: 'user',
            timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000 + 10 * 60 * 1000) // 6 dias atrás + 10 minutos
          },
          {
            id: '3-3',
            text: 'Claro! Trabalhamos com várias opções de financiamento imobiliário. Você já tem algum imóvel específico em mente ou está buscando informações gerais?',
            sender: 'bot',
            timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000) // 6 dias atrás + 15 minutos
          },
          {
            id: '3-4',
            text: 'Informações gerais mesmo. Qual a entrada mínima e documentos necessários?',
            sender: 'user',
            timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000) // 6 dias atrás + 30 minutos
          },
          {
            id: '3-5',
            text: 'Para financiamento imobiliário, geralmente a entrada mínima é de 20% do valor do imóvel. Quanto aos documentos, você precisará de: RG, CPF, comprovante de residência, comprovante de renda dos últimos 3 meses, declaração de imposto de renda e extrato bancário. Posso fazer uma simulação personalizada para você?',
            sender: 'bot',
            timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000 + 35 * 60 * 1000) // 6 dias atrás + 35 minutos
          },
          {
            id: '3-6',
            text: 'Sim, gostaria de uma simulação. Como faço?',
            sender: 'user',
            timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000 + 10 * 60 * 1000) // 5 dias atrás + 10 minutos
          },
          {
            id: '3-7',
            text: 'Vou enviar os documentos necessários para a simulação de financiamento por e-mail.',
            sender: 'bot',
            timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000) // 5 dias atrás
          }
        ]
      };
      
      // Adicionar as conversas de exemplo ao estado
      setConversationHistory([conversation1, conversation2, conversation3]);
      
      // Salvar as conversas de exemplo no localStorage
      saveConversationHistory([conversation1, conversation2, conversation3]);
    } else {
      setConversationHistory(conversationHistoryData);
    }
  }, []);
  
  // Função para formatar tempo relativo
  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) {
      return 'agora';
    } else if (diffMin < 60) {
      return `${diffMin} min atrás`;
    } else if (diffHour < 24) {
      return `${diffHour} h atrás`;
    } else if (diffDay < 7) {
      return `${diffDay} dia${diffDay > 1 ? 's' : ''} atrás`;
    } else {
      return new Date(date).toLocaleDateString();
    }
  };
  
  // Função para adicionar uma nova conversa
  const handleAddNewConversation = async () => {
    if (!newConversation.customerName || !newConversation.customerPhone) {
      setSnackbar({
        open: true,
        message: 'Por favor, preencha o nome e telefone do cliente',
        severity: 'error'
      });
      return;
    }

    try {
      // Criar nova conversa
      const initialMessage = newConversation.initialMessage || 'Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?';
      
      const newConv: Conversation = {
        id: Date.now().toString(),
        customerName: newConversation.customerName,
        customerPhone: newConversation.customerPhone,
        lastMessage: initialMessage,
        timestamp: new Date(),
        messages: [
          {
            id: Date.now().toString(),
            text: initialMessage,
            sender: 'bot',
            timestamp: new Date()
          }
        ],
        status: 'active'
      };
      
      // Atualizar o histórico de conversas
      const updatedHistory = [...conversationHistory, newConv];
      setConversationHistory(updatedHistory);
      saveConversationHistory(updatedHistory);
      
      // Selecionar a nova conversa
      setSelectedConversation(newConv.id);
      
      // Limpar o formulário
      setNewConversation({
        customerName: '',
        customerPhone: '',
        initialMessage: ''
      });
      
      // Fechar o diálogo
      setOpenNewConversationDialog(false);
      
      // Enviar mensagem via WhatsApp (simulação)
      try {
        await whatsappService.sendMessage(newConversation.customerPhone, initialMessage, {});
        setSnackbar({
          open: true,
          message: 'Conversa iniciada com sucesso!',
          severity: 'success'
        });
      } catch (error) {
        console.error('Erro ao enviar mensagem via WhatsApp:', error);
        setSnackbar({
          open: true,
          message: 'Conversa criada, mas houve um erro ao enviar a mensagem inicial via WhatsApp',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Erro ao criar nova conversa:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar nova conversa',
        severity: 'error'
      });
    }
  };
  
  // Função para enviar resposta a uma conversa
  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;
    
    const conversation = conversationHistory.find(c => c.id === selectedConversation);
    if (!conversation) return;
    
    setIsGeneratingResponse(true);
    
    try {
      // Gerar resposta usando RAG
      const response = await generateRAGResponse(replyMessage, {
        temperature: 0.7,
        maxTokens: 500,
        model: 'gpt-3.5-turbo'
      });
      
      // Adicionar mensagem do usuário à conversa
      const userMessage: ChatMessage = {
        id: `user_${Date.now()}`,
        text: replyMessage,
        sender: 'user',
        timestamp: new Date()
      };
      
      // Adicionar mensagem do bot à conversa
      const botMessage: ChatMessage = {
        id: `bot_${Date.now()}`,
        text: response,
        sender: 'bot',
        timestamp: new Date()
      };
      
      // Atualizar a conversa com as novas mensagens
      const updatedConversation: Conversation = {
        ...conversation,
        lastMessage: response,
        timestamp: new Date(),
        messages: [...conversation.messages, userMessage, botMessage]
      };
      
      // Atualizar o histórico de conversas
      const updatedHistory = conversationHistory.map(c => 
        c.id === selectedConversation ? updatedConversation : c
      );
      
      setConversationHistory(updatedHistory);
      saveConversationHistory(updatedHistory);
      
      // Limpar o campo de resposta
      setReplyMessage('');
      
      // Enviar mensagem via WhatsApp (simulação)
      try {
        await whatsappService.sendMessage(conversation.customerPhone, response, {});
        setSnackbar({
          open: true,
          message: 'Resposta enviada com sucesso!',
          severity: 'success'
        });
      } catch (error) {
        console.error('Erro ao enviar resposta via WhatsApp:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao enviar resposta via WhatsApp',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao gerar resposta:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao gerar resposta',
        severity: 'error'
      });
    } finally {
      setIsGeneratingResponse(false);
    }
  };
  
  // Função para alternar o status de uma conversa (ativar/desativar)
  const handleToggleConversationStatus = (conversationId: string) => {
    // Encontrar a conversa
    const conversation = conversationHistory.find(c => c.id === conversationId);
    if (!conversation) return;
    
    // Alternar o status
    const newStatus = conversation.status === 'active' ? 'closed' : 'active';
    
    // Atualizar o histórico de conversas
    const updatedHistory = conversationHistory.map(c => 
      c.id === conversationId ? { ...c, status: newStatus as 'active' | 'closed' } : c
    );
    
    setConversationHistory(updatedHistory);
    saveConversationHistory(updatedHistory);
    
    // Mostrar mensagem de sucesso
    setSnackbar({
      open: true,
      message: `Conversa ${newStatus === 'active' ? 'reativada' : 'encerrada'} com sucesso!`,
      severity: 'success'
    });
  };
  
  // Função para excluir uma conversa
  const handleDeleteConversation = (conversationId: string) => {
    // Confirmar exclusão
    if (!window.confirm('Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    // Remover a conversa do histórico
    const updatedHistory = conversationHistory.filter(c => c.id !== conversationId);
    
    // Se a conversa excluída estiver selecionada, limpar a seleção
    if (selectedConversation === conversationId) {
      setSelectedConversation('');
    }
    
    setConversationHistory(updatedHistory);
    saveConversationHistory(updatedHistory);
    
    // Mostrar mensagem de sucesso
    setSnackbar({
      open: true,
      message: 'Conversa excluída com sucesso!',
      severity: 'success'
    });
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon fontSize="large" />
          Histórico de Conversas
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => setOpenNewConversationDialog(true)}
        >
          Nova Conversa
        </Button>
      </Box>
      
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Lista de Conversas */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ height: 600, overflow: 'auto', p: 1 }}>
              {conversationHistory.length === 0 ? (
                <Box sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 1,
                  color: 'text.secondary'
                }}>
                  <MessageIcon sx={{ fontSize: 40 }} />
                  <Typography>
                    Nenhuma conversa encontrada
                  </Typography>
                </Box>
              ) : (
                <List>
                  {conversationHistory.map((conversation) => (
                    <React.Fragment key={conversation.id}>
                      <ListItem 
                        button 
                        selected={selectedConversation === conversation.id}
                        onClick={() => setSelectedConversation(conversation.id)}
                      >
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: conversation.status === 'active' ? 'primary.main' : 'text.disabled' }}>
                            {conversation.customerName.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {conversation.customerName}
                              <Chip 
                                size="small" 
                                label={conversation.status === 'active' ? 'Ativa' : 'Encerrada'} 
                                color={conversation.status === 'active' ? 'success' : 'default'}
                              />
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" noWrap>
                                {conversation.lastMessage.length > 30 
                                  ? `${conversation.lastMessage.substring(0, 30)}...` 
                                  : conversation.lastMessage}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  📱 {conversation.customerPhone}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  🕒 {formatRelativeTime(conversation.timestamp)}
                                </Typography>
                              </Box>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
          
          {/* Detalhe da Conversa */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ height: 600, display: 'flex', flexDirection: 'column' }}>
              {!selectedConversation ? (
                <Box sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 1,
                  color: 'text.secondary'
                }}>
                  <WhatsAppIcon sx={{ fontSize: 48 }} />
                  <Typography>
                    Selecione uma conversa para ver os detalhes
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Cabeçalho da conversa */}
                  {(() => {
                    const selectedConv = conversationHistory.find(c => c.id === selectedConversation);
                    if (!selectedConv) return null;
                    
                    return (
                      <>
                        <Box sx={{ 
                          p: 2, 
                          borderBottom: '1px solid', 
                          borderColor: 'divider',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Box>
                            <Typography variant="h6">
                              {selectedConv.customerName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedConv.customerPhone}
                            </Typography>
                          </Box>
                          <Box>
                            <Button
                              variant="outlined"
                              color={selectedConv.status === 'active' ? 'error' : 'primary'}
                              size="small"
                              onClick={() => handleToggleConversationStatus(selectedConv.id)}
                              sx={{ mr: 1 }}
                            >
                              {selectedConv.status === 'active' ? 'Encerrar' : 'Reabrir'}
                            </Button>
                            <IconButton 
                              color="error" 
                              onClick={() => handleDeleteConversation(selectedConv.id)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        
                        {/* Mensagens */}
                        <Box sx={{ 
                          flexGrow: 1, 
                          overflowY: 'auto',
                          p: 2,
                          bgcolor: '#f5f5f5',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1
                        }}>
                          {selectedConv.messages.map((message) => (
                            <Box 
                              key={message.id}
                              sx={{
                                maxWidth: '70%',
                                alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
                                bgcolor: message.sender === 'user' ? 'primary.light' : 'white',
                                color: message.sender === 'user' ? 'white' : 'text.primary',
                                p: 1.5,
                                borderRadius: 2,
                                boxShadow: 1
                              }}
                            >
                              <Typography variant="body1">
                                {message.text}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', textAlign: 'right', mt: 0.5 }}>
                                {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        
                        {/* Campo de resposta */}
                        {selectedConv.status === 'active' && (
                          <Box sx={{ 
                            p: 2, 
                            borderTop: '1px solid', 
                            borderColor: 'divider',
                            display: 'flex',
                            gap: 1
                          }}>
                            <TextField
                              fullWidth
                              placeholder="Digite uma resposta..."
                              size="small"
                              value={replyMessage}
                              onChange={(e) => setReplyMessage(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSendReply();
                                }
                              }}
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={!replyMessage.trim() || isGeneratingResponse}
                              onClick={handleSendReply}
                            >
                              {isGeneratingResponse ? <CircularProgress size={24} /> : 'Enviar'}
                            </Button>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Diálogo para adicionar nova conversa */}
      <Dialog 
        open={openNewConversationDialog} 
        onClose={() => setOpenNewConversationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Nova Conversa
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome do Cliente"
              fullWidth
              value={newConversation.customerName}
              onChange={(e) => setNewConversation({ ...newConversation, customerName: e.target.value })}
              required
            />
            <TextField
              label="Telefone do Cliente"
              fullWidth
              value={newConversation.customerPhone}
              onChange={(e) => setNewConversation({ ...newConversation, customerPhone: e.target.value })}
              placeholder="Ex: 5511999999999"
              helperText="Inclua o código do país (Ex: 55 para Brasil)"
              required
            />
            <TextField
              label="Mensagem Inicial"
              fullWidth
              multiline
              rows={3}
              value={newConversation.initialMessage}
              onChange={(e) => setNewConversation({ ...newConversation, initialMessage: e.target.value })}
              placeholder="Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?"
              helperText="Deixe em branco para usar a mensagem padrão"
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Mensagens Rápidas
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {templates.map(template => (
                  <Chip
                    key={template.id}
                    label={template.name}
                    onClick={() => setNewConversation({ 
                      ...newConversation, 
                      initialMessage: template.content 
                    })}
                    clickable
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewConversationDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddNewConversation}
            disabled={!newConversation.customerName || !newConversation.customerPhone}
          >
            Criar Conversa
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConversationHistoryPage;
